<template>
    <div>
        <Head :title="__('Update Ticket Details')" />

        <div class="bg-primary pt-[100px] pb-12">
            <div class="container mx-auto px-4">

            </div>
        </div>

        <div class="container mx-auto px-4 py-8">
            <div class="card bg-white rounded-lg shadow-md overflow-hidden">
                <div class="p-6">
                      <h1 class="text-3xl font-bold text-white text-center">
                    {{ __('Update Your Ticket Details') }}
                </h1>

                    <div class="mb-6">
                        <h2 class="text-xl font-bold mb-2">Ticket Reference: {{ ticket.uid }}</h2>
                        <p class="text-gray-600">Status: {{ ticket.status }}</p>
                        <p class="text-gray-600">Created: {{ formatDate(ticket.created_at) }}</p>
                    </div>

                    <form @submit.prevent="update">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <text-input
                                v-model="form.subject"
                                :error="form.errors.subject"
                                class="w-full"
                                :label="__('Subject')"
                                required
                            />

                            <div class="mb-4">
                                <label class="form-label">{{ __('Date of Incident') }}</label>
                                <input
                                    type="date"
                                    v-model="form.date_of_incident"
                                    :error="form.errors.date_of_incident"
                                    class="form-input w-full"
                                    required
                                />
                                <div v-if="form.errors.date_of_incident" class="form-error">
                                    {{ form.errors.date_of_incident }}
                                </div>
                            </div>
                        </div>

                        <div class="mb-8 w-full">
                            <label class="form-label">{{ __('Complaint Details') }}</label>
                            <ckeditor
                                id="ticketDetails"
                                :editor="editor"
                                v-model="form.details"
                                :config="editorConfig"
                            ></ckeditor>
                            <div v-if="form.errors.details" class="form-error">
                                {{ form.errors.details }}
                            </div>
                        </div>

                        <!-- File Upload Section -->
                        <div class="mb-8 w-full flex-col">
                            <input
                                ref="file"
                                type="file"
                                accept=".xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf, .zip"
                                class="hidden"
                                multiple="multiple"
                                @change="fileInputChange"
                            />

                            <button
                                type="button"
                                class="btn flex justify-center items-center mb-2"
                                @click="fileBrowse"
                            >
                                <icon name="file" class="flex-shrink-0 h-8 fill-gray-400 pr-1" />
                                <h4>{{ __('Attach files') }}</h4>
                            </button>

                            <p class="text-sm text-gray-700 dark:text-gray-300 mb-2 font-medium">
                                {{ __('Maximum 5 files, 5MB each, 20MB total') }}
                            </p>

                            <div v-if="form.files.length" class="space-y-2">
                                <div
                                    v-for="(file, fi) in form.files"
                                    :key="fi"
                                    class="flex items-center justify-between p-2 border rounded"
                                >
                                    <div class="flex-1 pr-1 flex items-center">
                                        <icon name="file" class="flex-shrink-0 h-5 fill-gray-400 mr-2" />
                                        <div>
                                            <div class="text-sm">{{ file.name }}</div>
                                            <div class="text-xs text-gray-500">{{ getFileSize(file.size) }}</div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        @click="fileRemove(file, fi)"
                                        class="text-red-500 hover:text-red-700"
                                    >
                                        <icon name="trash" class="h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Existing Attachments -->
                        <div v-if="ticket.attachments.length" class="mb-8">
                            <h3 class="font-bold mb-2">{{ __('Current Attachments') }}</h3>
                            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                <div
                                    v-for="attachment in ticket.attachments"
                                    :key="attachment.id"
                                    class="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                                >
                                    <a
                                        :href="`/storage/${attachment.path}`"
                                        target="_blank"
                                        class="flex items-center text-blue-600 hover:text-blue-800 transition duration-200"
                                    >
                                        <icon name="file" class="w-5 h-5 mr-2" />
                                        <span class="truncate">{{ attachment.name }}</span>
                                    </a>
                                    <p class="text-xs text-gray-500 mt-1">{{ getFileSize(attachment.size) }}</p>
                                    <button
                                        type="button"
                                        @click="removeAttachment(attachment.id)"
                                        class="mt-2 text-sm text-red-600 hover:text-red-800"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="flex justify-end">
                            <loading-button
                                :loading="form.processing"
                                class="rounded-lg bg-primary py-3 px-6 text-base font-medium text-white duration-300 ease-in-out hover:bg-opacity-80"
                            >
                                {{ __('Update Ticket') }}
                            </loading-button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import Layout from '@/Shared/Landing/Layout';
import Icon from '@/Shared/Icon';
import TextInput from '@/Shared/TextInput';
import LoadingButton from '@/Shared/LoadingButton';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CKEditor from '@ckeditor/ckeditor5-vue';

export default {
    components: {
        Head,
        Icon,
        TextInput,
        LoadingButton,
        ckeditor: CKEditor.component,
    },
    layout: Layout,
    props: {
        ticket: Object,
    },
    data() {
        return {
            editor: ClassicEditor,
            editorConfig: {
                toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'outdent', 'indent', '|', 'insertTable', 'blockQuote', '|', 'undo', 'redo'],
                table: {
                    toolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
                },
            },
            form: this.$inertia.form({
                subject: this.ticket.subject,
                date_of_incident: this.ticket.date_of_incident,
                details: this.ticket.details,
                files: [],
                removed_attachments: [],
            }),
        };
    },
    methods: {
        formatDate(date) {
            return new Date(date).toLocaleDateString();
        },
        fileBrowse() {
            this.$refs.file.click();
        },
        fileInputChange(e) {
            const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB per file
            const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20MB total

            // Calculate current total size
            const currentTotal = this.form.files.reduce((sum, file) => sum + file.size, 0);

            // Check each new file
            for (const file of e.target.files) {
                // Check individual file size
                if (file.size > MAX_FILE_SIZE) {
                    alert(`Error: "${file.name}" exceeds 5MB limit per file`);
                    return;
                }

                // Check total size after adding this file
                if (currentTotal + file.size > MAX_TOTAL_SIZE) {
                    alert(`Error: Adding "${file.name}" would exceed 20MB total limit`);
                    return;
                }

                // Check maximum number of files
                if (this.form.files.length >= 5) {
                    alert('Maximum 5 files allowed');
                    return;
                }

                // Check allowed file types
                const allowedTypes = [
                    'xlsx', 'xls', 'doc', 'docx', 'ppt', 'pptx',
                    'txt', 'pdf', 'zip', 'jpg', 'jpeg', 'png', 'gif'
                ];
                const fileExt = file.name.split('.').pop().toLowerCase();

                if (!allowedTypes.includes(fileExt)) {
                    alert(`File type .${fileExt} not allowed`);
                    return;
                }

                this.form.files.push(file);
            }

            // Reset input to allow selecting same files again if needed
            e.target.value = '';
        },
        fileRemove(file, index) {
            this.form.files.splice(index, 1);
        },
        removeAttachment(attachmentId) {
            if (confirm('Are you sure you want to remove this attachment?')) {
                this.form.removed_attachments.push(attachmentId);
            }
        },
        getFileSize(size) {
            const i = Math.floor(Math.log(size) / Math.log(1024));
            return (size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
        },
        update() {
            this.form.post(route('tickets.update_public', this.ticket.uid), {
                onSuccess: () => {
                    this.$toast.success('Ticket updated successfully');
                },
                onError: () => {
                    this.$toast.error('There was an error updating your ticket');
                }
            });
        },
    },
};
</script>
